import axios from 'axios';

/**
 * Single axios instance for the Express backend.
 * - Dev: CRA proxy (package.json "proxy") forwards /api to localhost:5000.
 * - Prod/mobile: set REACT_APP_API_URL to the deployed backend origin.
 */
const TOKEN_KEY = 'vgo_token';
const USER_KEY = 'vgo_user';
export const SESSION_EXPIRED_EVENT = 'vgo:session-expired';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 20000,
});

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Some embedded/private browser modes disable storage. The caller can
    // still finish the current request without crashing the whole app.
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // Storage may be unavailable in hardened WebViews/private browsing.
  }
}

export function getCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setCachedUser(user) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    // Keep the in-memory AuthContext usable even when persistence is blocked.
  }
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Terms action gate (428 Precondition Required). TermsAgreementGate registers
 * a handler that opens the terms modal and resolves once the vendor accepts;
 * the blocked request is then retried transparently. One retry only - if the
 * gate still fires after acceptance, surface the error instead of looping.
 */
let termsGateHandler = null;
export function setTermsGateHandler(handler) {
  termsGateHandler = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    // Expired/invalid session: drop credentials so RequireAuth kicks the
    // user to login. A plain 401 may just mean a wrong current password, so
    // only the auth middleware's explicit code is allowed to end a session.
    if (status === 401 && code === 'SESSION_INVALID' && getToken()) {
      clearToken();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
    }
    if (status === 428 && code === 'TERMS_AGREEMENT_REQUIRED'
        && termsGateHandler && !error.config?.__termsRetried) {
      return termsGateHandler(error.response.data).then(
        () => api.request({ ...error.config, __termsRetried: true }),
        () => Promise.reject(error) // modal dismissed without accepting
      );
    }
    return Promise.reject(error);
  }
);

/**
 * True when the request never reached the VGO backend: a genuine network
 * failure, or a dev-proxy/gateway failure (5xx with an HTML/text body -
 * the real API always answers errors as JSON `{message}`).
 */
export function isNetworkError(err) {
  if (!err) return false;
  if (!err.response) return true;
  const contentType = String(err.response.headers?.['content-type'] || '');
  return err.response.status >= 500 && !contentType.includes('application/json');
}

/**
 * Sandbox demo mode: on for dev builds (npm start), OFF for production builds
 * (npm run build) so an unreachable backend surfaces as a real error instead
 * of silent demo data. REACT_APP_ENABLE_SANDBOX=true|false overrides either way.
 */
export const SANDBOX_ENABLED =
  process.env.REACT_APP_ENABLE_SANDBOX === 'true' ||
  (process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENABLE_SANDBOX !== 'false');

/** Fallback gate: the request died off-backend AND demo mode is allowed. */
export function shouldSandbox(err) {
  return SANDBOX_ENABLED && isNetworkError(err);
}

/** Human-readable message from an axios error (backend sends {message}). */
export function errorMessage(err, fallback = 'Something went wrong. Please try again.') {
  return err?.response?.data?.message || err?.message || fallback;
}
